import React, { useState, useEffect, useMemo } from "react";
import { Table, Input, Button, Modal, Form, Switch, Select, Tooltip, message } from "antd";
import {
  SearchOutlined, PlusOutlined, EditOutlined,
  MenuOutlined, FileTextOutlined, FileExcelOutlined,
} from "@ant-design/icons";
import { apiRequest } from "../../../utilities/apiMethod";
import { useThemeStore } from "../../../store/useThemeStore";
import { colSearch, FormField, StatusTag, actionBtnStyle, exportCSV, exportExcel } from "../../Master/masterUtils";

const INPUT_H = { style: { borderRadius: 8, height: 40 } };

export default function MenuSettings() {
  const { themeColor } = useThemeStore();
  const [data,          setData]          = useState([]);
  const [menuGroupData, setMenuGroupData] = useState([]);
  const [formData,      setFormData]      = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [search,        setSearch]        = useState("");
  const [pagination,    setPagination]    = useState({ current: 1, pageSize: 10 });
  const [modalOpen,     setModalOpen]     = useState(false);
  const [editRec,       setEditRec]       = useState(null);
  const [saving,        setSaving]        = useState(false);
  const [form]                            = Form.useForm();

  useEffect(() => {
    Promise.all([fetchData(), fetchMenuGroupData(), fetchFormData()]);
  }, []);

  async function fetchData() {
    setLoading(true);
    try {
      const res = await apiRequest("GET", "Menus");
      if (res?.IsSuccess && Array.isArray(res.Data)) {
        setData(res.Data.map(el => ({
          ...el,
          labelMenuGroup:  el?.MenuGroup?.GroupName  ?? "",
          labelParentMenu: el?.ParentMenu?.MenuName  ?? "",
        })));
      } else { setData([]); }
    } finally { setLoading(false); }
  }

  async function fetchMenuGroupData() {
    try {
      const res = await apiRequest("GET", "MenuGroups");
      setMenuGroupData(res?.IsSuccess && Array.isArray(res.Data) ? res.Data : []);
    } catch { setMenuGroupData([]); }
  }

  async function fetchFormData() {
    try {
      const res = await apiRequest("GET", "Forms");
      setFormData(res?.IsSuccess && Array.isArray(res.Data) ? res.Data : []);
    } catch { setFormData([]); }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter(r =>
      r.MenuName?.toLowerCase().includes(q) ||
      r.labelMenuGroup?.toLowerCase().includes(q) ||
      r.labelParentMenu?.toLowerCase().includes(q)
    );
  }, [data, search]);

  function openAdd() { setEditRec(null); form.resetFields(); setModalOpen(true); }
  function openEdit(rec) {
    setEditRec(rec);
    form.setFieldsValue({
      menuName:     rec.MenuName,
      parentMenuId: rec.ParentMenuId !== 0 ? rec.ParentMenuId : null,
      menuGroupId:  rec.MenuGroupId  !== 0 ? rec.MenuGroupId  : null,
      displayOrder: rec.DisplayOrder,
      formId:       rec.FormId       !== 0 ? rec.FormId       : null,
      isActive:     rec.IsActive,
    });
    setModalOpen(true);
  }
  function closeModal() { setModalOpen(false); setSaving(false); }

  async function onFinish(values) {
    setSaving(true);
    try {
      const isEdit  = !!editRec;
      const payload = {
        menuName:     values.menuName,
        parentMenuId: values.parentMenuId ?? "",
        menuGroupId:  values.menuGroupId  ?? "",
        url:          "",
        icon:         "",
        displayOrder: parseInt(values.displayOrder) || 0,
        formId:       values.formId ?? "",
        isActive:     !!values.isActive,
      };
      const res = await apiRequest(isEdit ? "PUT" : "POST", isEdit ? `Menus/${editRec.MenuId}` : "Menus", payload);
      if (res?.IsSuccess) {
        message.success(isEdit ? "Menu updated." : "Menu created.");
        closeModal(); fetchData();
      } else { message.error(res?.displayMessage || "Something went wrong."); }
    } catch { message.error("Request failed."); }
    finally  { setSaving(false); }
  }

  const columns = [
    {
      title: "S.No", key: "sno", width: 64, align: "center",
      render: (_, __, i) => <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}>{(pagination.current - 1) * pagination.pageSize + i + 1}</span>,
    },
    {
      title: "Menu Name", dataIndex: "MenuName", key: "MenuName",
      sorter: (a, b) => a.MenuName?.localeCompare(b.MenuName),
      ...colSearch("MenuName", "Menu Name", themeColor),
      render: v => <span style={{ fontWeight: 600, color: "#111827" }}>{v}</span>,
    },
    {
      title: "Display Order", dataIndex: "DisplayOrder", key: "DisplayOrder", width: 130, align: "center",
      sorter: (a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0),
      render: v => <span style={{ fontFamily: "monospace", fontSize: 13, color: "#374151" }}>{v}</span>,
    },
    {
      title: "Parent Menu", dataIndex: "labelParentMenu", key: "labelParentMenu",
      ...colSearch("labelParentMenu", "Parent Menu", themeColor),
      render: v => <span style={{ color: "#374151" }}>{v || "—"}</span>,
    },
    {
      title: "Menu Group", dataIndex: "labelMenuGroup", key: "labelMenuGroup",
      ...colSearch("labelMenuGroup", "Menu Group", themeColor),
      render: v => <span style={{ color: "#374151" }}>{v || "—"}</span>,
    },
    {
      title: "Status", dataIndex: "IsActive", key: "IsActive", width: 110, align: "center",
      filters: [{ text: "Active", value: true }, { text: "Inactive", value: false }],
      onFilter: (val, rec) => rec.IsActive === val,
      render: v => <StatusTag active={v} />,
    },
    {
      title: "Action", key: "action", width: 80, align: "center",
      render: (_, rec) => (
        <Tooltip title="Edit" placement="top">
          <button
            onClick={() => openEdit(rec)}
            style={actionBtnStyle(themeColor)}
            onMouseEnter={e => Object.assign(e.currentTarget.style, { background: "#f0f4ff", borderColor: themeColor + "55" })}
            onMouseLeave={e => Object.assign(e.currentTarget.style, { background: "#f9fafb", borderColor: "#e5e7eb" })}
          >
            <EditOutlined style={{ fontSize: 14 }} />
          </button>
        </Tooltip>
      ),
    },
  ];

  return (
    <div style={{ padding: "4px 0" }}>

      {/* Page header */}
      <div style={{
        background: "#fff", borderRadius: 14, border: "1px solid #f0f1f3",
        boxShadow: "0 1px 6px rgba(0,0,0,0.04)", padding: "12px 18px",
        marginBottom: 10, display: "flex", alignItems: "center",
        justifyContent: "space-between", gap: 16,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10, flexShrink: 0,
            background: themeColor, display: "flex", alignItems: "center",
            justifyContent: "center", boxShadow: `0 4px 14px ${themeColor}44`,
            transition: "background 0.3s",
          }}>
            <MenuOutlined style={{ fontSize: 17, color: "#fff" }} />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#111827" }}>Menu Settings</h2>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 3 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: themeColor }}>{data.length}</span>
              <span style={{ fontSize: 13, color: "#6b7280" }}>Total</span>
              <span style={{ width: 1, height: 12, background: "#e5e7eb", display: "inline-block" }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: "#16a34a" }}>{data.filter(r => r.IsActive).length}</span>
              <span style={{ fontSize: 13, color: "#6b7280" }}>Active</span>
              <span style={{ width: 1, height: 12, background: "#e5e7eb", display: "inline-block" }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: "#dc2626" }}>{data.filter(r => !r.IsActive).length}</span>
              <span style={{ fontSize: 13, color: "#6b7280" }}>Inactive</span>
            </div>
          </div>
        </div>
        <Button
          icon={<PlusOutlined />} onClick={openAdd}
          style={{ height: 38, borderRadius: 9, fontWeight: 600, fontSize: 13, background: themeColor, borderColor: themeColor, color: "#fff", boxShadow: `0 2px 8px ${themeColor}44`, flexShrink: 0, transition: "background 0.3s" }}
        >
          Add Menu
        </Button>
      </div>

      {/* Table card */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #f0f1f3", boxShadow: "0 1px 6px rgba(0,0,0,0.04)", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderBottom: "1px solid #f0f1f3", flexWrap: "wrap", gap: 8 }}>
          <Input
            placeholder="Search menu name, group or parent…"
            prefix={<SearchOutlined style={{ color: "#c4c7cd" }} />}
            value={search} onChange={e => { setSearch(e.target.value); setPagination(p => ({ ...p, current: 1 })); }}
            allowClear style={{ width: 320, borderRadius: 8, height: 34 }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 13, color: "#6b7280" }}>{filtered.length} record{filtered.length !== 1 ? "s" : ""}</span>
            <Button icon={<FileTextOutlined />} onClick={() => exportCSV(filtered.map((r, i) => ({ "S.No": i + 1, "Menu Name": r.MenuName, "Display Order": r.DisplayOrder, "Parent Menu": r.labelParentMenu, "Menu Group": r.labelMenuGroup, "Status": r.IsActive ? "Active" : "Inactive" })), "menu_settings.csv")} style={{ height: 32, borderRadius: 8, fontWeight: 600, borderColor: "#22c55e", color: "#16a34a", fontSize: 12 }}>CSV</Button>
            <Button icon={<FileExcelOutlined />} onClick={() => exportExcel(filtered.map((r, i) => ({ "S.No": i + 1, "Menu Name": r.MenuName, "Display Order": r.DisplayOrder, "Parent Menu": r.labelParentMenu, "Menu Group": r.labelMenuGroup, "Status": r.IsActive ? "Active" : "Inactive" })), "menu_settings.xlsx")} style={{ height: 32, borderRadius: 8, fontWeight: 600, borderColor: "#16a34a", color: "#15803d", fontSize: 12 }}>Excel</Button>
          </div>
        </div>
        <Table
          rowKey="MenuId"
          columns={columns}
          dataSource={filtered}
          loading={loading}
          size="small"
          scroll={{ x: 900 }}
          onChange={pag => setPagination({ current: pag.current, pageSize: pag.pageSize })}
          pagination={{
            ...pagination, total: filtered.length,
            showSizeChanger: true, pageSizeOptions: ["10", "20", "50"],
            showTotal: (t, r) => `Showing ${r[0]}–${r[1]} of ${t}`,
            style: { padding: "8px 14px", margin: 0 },
          }}
        />
      </div>

      {/* Add / Edit modal */}
      <Modal
        open={modalOpen} onCancel={closeModal} footer={null}
        width={520} centered destroyOnClose
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: "#f4f5f7", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {editRec ? <EditOutlined style={{ fontSize: 14, color: themeColor }} /> : <PlusOutlined style={{ fontSize: 14, color: themeColor }} />}
            </div>
            <div>
              <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#111827", lineHeight: 1.2 }}>
                {editRec ? "Edit Menu" : "Add Menu"}
              </p>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: "#6b7280", fontWeight: 400 }}>
                {editRec ? `Editing ${editRec.MenuName}` : "Create a new menu item"}
              </p>
            </div>
          </div>
        }
      >
        <Form form={form} layout="vertical" onFinish={onFinish} requiredMark={false} style={{ marginTop: 8 }}>
          <FormField name="menuName" label="Menu Name" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="e.g. Country Master" {...INPUT_H} />
          </FormField>
          <FormField name="menuGroupId" label="Menu Group" rules={[{ required: true, message: "Required" }]}>
            <Select
              placeholder="Select menu group"
              options={menuGroupData.map(g => ({ value: g.MenuGroupId, label: g.GroupName }))}
              allowClear showSearch optionFilterProp="label"
              style={{ borderRadius: 8, height: 40 }}
            />
          </FormField>
          <FormField name="parentMenuId" label="Parent Menu">
            <Select
              placeholder="Select parent menu (optional)"
              options={data.map(m => ({ value: m.MenuId, label: m.MenuName }))}
              allowClear showSearch optionFilterProp="label"
              style={{ borderRadius: 8, height: 40 }}
            />
          </FormField>
          <FormField name="formId" label="Form">
            <Select
              placeholder="Select form (optional)"
              options={[{ value: 0, label: "NA" }, ...formData.map(f => ({ value: f.FormId, label: f.FormName }))]}
              allowClear showSearch optionFilterProp="label"
              style={{ borderRadius: 8, height: 40 }}
            />
          </FormField>
          <FormField name="displayOrder" label="Display Order">
            <Input type="number" min={0} placeholder="e.g. 1" {...INPUT_H} />
          </FormField>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", borderRadius: 8, border: "1px solid #f0f1f3", background: "#fafafa", marginBottom: 20 }}>
            <div>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#111827" }}>Active</p>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: "#6b7280" }}>Enable or disable this menu</p>
            </div>
            <Form.Item name="isActive" valuePropName="checked" noStyle initialValue={false}>
              <Switch />
            </Form.Item>
          </div>
          <div style={{ display: "flex", gap: 10, paddingTop: 4, borderTop: "1px solid #f0f1f3" }}>
            <Button onClick={closeModal} style={{ flex: 1, height: 40, borderRadius: 8 }}>Cancel</Button>
            <Button htmlType="submit" loading={saving} style={{ flex: 2, height: 40, borderRadius: 8, fontWeight: 700, background: themeColor, borderColor: themeColor, color: "#fff", transition: "background 0.3s" }}>
              {editRec ? "Save Changes" : "Create Menu"}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
